import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, CheckCircle, Phone, Cloud, Network, Lock, Zap } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Button, Badge, AnimatedCard, CardHeader, CardTitle, CardDescription } from '@/components/ui'
import { CTASection } from '@/components/sections'
import { getServiceById } from '@/data/services'
import { companyInfo } from '@/data/company'

export function SDWAN() {
  const service = getServiceById('sd-wan')!

  return (
    <>
      <SEO
        title="SD-WAN Solutions"
        description="Transform your WAN with intelligent, application-aware routing that optimizes performance and reduces costs."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="glow" className="mb-6">SD-WAN Solutions</Badge>
              
              <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
                {service.title}
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                {service.longDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" variant="glow" className="group">
                    Get SD-WAN Quote
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phoneRaw}`}>
                  <Button size="lg" variant="secondary">
                    <Phone className="w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="hidden lg:block"
            >
              <div className="glass-card p-8 rounded-3xl">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Zap, label: 'App-Aware', desc: 'Smart traffic routing' },
                    { icon: Network, label: 'Multi-Link', desc: 'Link aggregation' },
                    { icon: Cloud, label: 'Cloud-Ready', desc: 'Direct cloud access' },
                    { icon: Lock, label: 'Secure', desc: 'Built-in encryption' },
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                      className="glass-card-hover p-6"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center mb-4 shadow-glow">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-white mb-1">{item.label}</h3>
                      <p className="text-sm text-slate-400">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Savings Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Cost Savings</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Reduce WAN Costs Up to 70%
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Replace expensive MPLS circuits with intelligent SD-WAN technology that delivers better performance at a fraction of the cost.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { value: '70%', label: 'Cost Reduction', desc: 'Average savings vs MPLS' },
              { value: '3x', label: 'Bandwidth', desc: 'Increase in available bandwidth' },
              { value: '99.99%', label: 'Uptime', desc: 'With automatic failover' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-8 text-center"
              >
                <div className="text-4xl md:text-5xl font-heading font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-lg font-semibold text-white mb-1">{stat.label}</div>
                <div className="text-sm text-slate-400">{stat.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Features</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Intelligent WAN Management
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {service.features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <AnimatedCard className="h-full">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-7 h-7 text-accent-400" />
                    </div>
                    <CardTitle>{feature.title}</CardTitle>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </AnimatedCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-4">Benefits</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Why Choose Our SD-WAN Solutions?
              </h2>

              <div className="space-y-4">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-success-500" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass-card p-8 rounded-3xl"
            >
              <h3 className="text-xl font-heading font-semibold text-white mb-6">
                MPLS Replacement ROI Calculator
              </h3>
              <p className="text-slate-400 mb-6">
                Find out how much you could save by replacing your MPLS circuits with SD-WAN technology.
              </p>
              <Link to="/contact">
                <Button className="w-full" variant="glow">
                  Request Free Assessment
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Savings Deep Dive */}
      <section className="section-padding relative">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4">Cost Optimization</Badge>
              <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-6">
                Why Financial Institutions are <span className="text-gradient">Moving to SD-WAN</span>
              </h2>
              <p className="text-lg text-slate-400 mb-6">
                MPLS circuits are expensive and inflexible. Our SD-WAN solutions allow you to leverage high-speed internet connections with the same reliability and security as MPLS, at a fraction of the cost.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Bandwidth', desc: 'Increase by up to 10x' },
                  { label: 'Deployment', desc: 'Days instead of months' },
                  { label: 'Security', desc: 'Integrated SASE & Zero Trust' },
                  { label: 'Uptime', desc: 'Multi-link redundancy' },
                ].map((item) => (
                  <div key={item.label} className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="font-heading font-bold text-accent-400">{item.label}</div>
                    <div className="text-xs text-slate-500">{item.desc}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="glass-card p-8 rounded-3xl border-accent-500/20 shadow-glow"
            >
              <h3 className="text-xl font-heading font-bold text-white mb-6 text-center">Typical Savings Analysis</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Current MPLS Spend</span>
                  <span className="text-white">$12,000/mo</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-accent-400">
                  <span className="">SD-WAN Estimated Spend</span>
                  <span className="">$3,500/mo</span>
                </div>
                <div className="pt-4 border-t border-white/10">
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="text-xs text-slate-500 uppercase">Annual Savings</div>
                      <div className="text-3xl font-heading font-bold text-success-500">$102,000</div>
                    </div>
                    <Badge variant="glow" className="mb-1">71% Reduction</Badge>
                  </div>
                </div>
                <Link to="/contact" className="block pt-4">
                  <Button className="w-full">Get Your Savings Estimate</Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}

